import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, GraduationCap, Users, Sparkles, BookOpen, Palette, Cpu, Heart, Leaf, Briefcase, ChevronRight, Quote, Calendar, Megaphone, Flame, Instagram, Facebook, Youtube } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { useSettings } from "../lib/SettingsContext";

const ICONS = { "book-open": BookOpen, "flask-conical": Sparkles, "cpu": Cpu, "palette": Palette, "trophy": Trophy, "users": Users, "briefcase": Briefcase, "heart": Heart, "leaf": Leaf, sparkles: Sparkles };

function Counter({ end, suffix = "", testId }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let start = 0;
    const target = end || 0;
    const step = Math.max(1, Math.ceil(target / 60));
    const id = setInterval(() => {
      start += step;
      if (start >= target) { setV(target); clearInterval(id); } else setV(start);
    }, 24);
    return () => clearInterval(id);
  }, [end]);
  return <span data-testid={testId}>{v}{suffix}</span>;
}

export default function Home() {
  const { settings } = useSettings();
  const [news, setNews] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/news?limit=4").then(({ data }) => setNews(data.items)).catch(() => {});
    api.get("/achievements?limit=4").then(({ data }) => setAchievements(data.items)).catch(() => {});
    api.get("/programs?limit=6").then(({ data }) => setPrograms(data.items)).catch(() => {});
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const garam = [
    { letter: "G", title: "Generasi", subtitle: "Berakhlak Kristiani", description: "Berpegang teguh pada nilai-nilai iman, integritas, dan kasih sebagai pondasi karakter." },
    { letter: "A", title: "Apikal", subtitle: "Unggul & Berprestasi", description: "Berdaya saing global melalui akademik, olahraga, seni, dan sains berkualitas." },
    { letter: "R", title: "Responsif", subtitle: "Adaptif & Tanggap", description: "Siap menghadapi perubahan zaman dengan pikiran terbuka dan kolaboratif." },
    { letter: "M", title: "Mandiri", subtitle: "Sosial & IPTEK", description: "Berdikari, kreatif, dan berkontribusi nyata bagi masyarakat & lingkungan." },
  ];

  const pillars = [
    { icon: BookOpen, title: "Learn", desc: "Pembelajaran mendalam & kolaboratif berbasis Kurikulum Merdeka." },
    { icon: Palette, title: "Create", desc: "Menumbuhkan kreativitas melalui proyek, seni, dan inovasi digital." },
    { icon: Users, title: "Collaborate", desc: "Bekerja sama lintas tim dengan empati dan komunikasi efektif." },
    { icon: Sparkles, title: "Lead", desc: "Melatih kepemimpinan melalui OSIS, komunitas, dan proyek sosial." },
    { icon: Heart, title: "Serve", desc: "Melayani sesama dan lingkungan dengan nilai Kristiani." },
  ];

  const quickCards = [
    { icon: GraduationCap, title: "Profil Sekolah", to: "/tentang-kami", color: "bg-amber-50 text-amber-700" },
    { icon: BookOpen, title: "Program Akademik", to: "/akademik", color: "bg-blue-50 text-blue-700" },
    { icon: Trophy, title: "Prestasi Murid", to: "/prestasi", color: "bg-rose-50 text-rose-700" },
    { icon: Sparkles, title: "Ekstrakurikuler", to: "/kehidupan-sekolah", color: "bg-emerald-50 text-emerald-700" },
    { icon: Megaphone, title: "PPDB Online", to: "/ppdb", color: "bg-indigo-50 text-indigo-700" },
    { icon: Flame, title: "Berita Terbaru", to: "/berita", color: "bg-orange-50 text-orange-700" },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src={settings?.hero_image_url || settings?.building_image_url || "https://images.unsplash.com/photo-1769430886896-dc30842be5a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"}
            alt="SMAS YPK 2 Biak Campus"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 papua-pattern-dark opacity-40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 w-full">
          <div className="max-w-3xl animate-fade-up">
            <Badge className="bg-amber-500/15 border border-amber-400/40 text-amber-200 hover:bg-amber-500/20 rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wider uppercase mb-6">
              <Sparkles className="w-3 h-3 mr-1.5" /> Akreditasi A • NPSN 60301717 • Sejak 1991
            </Badge>
            <h1 data-testid="hero-headline" className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Mencetak Generasi <span className="text-amber-400 italic">Unggul</span>,<br/>
              Berkarakter, dan <span className="text-amber-400 italic">Berprestasi</span>.
            </h1>
            <p className="mt-7 text-lg text-slate-200/90 max-w-2xl leading-relaxed">
              <span className="font-semibold text-white">SMAS YPK 2 Biak</span> — tempat murid bertumbuh dalam ilmu, karakter, kreativitas, kepemimpinan, dan prestasi di tanah Papua.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/tentang-kami" data-testid="hero-cta-about">
                <Button size="lg" className="bg-white hover:bg-slate-50 text-[#0B1D3A] font-bold rounded-full px-7 h-12 shadow-xl">
                  Kenali Sekolah Kami <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/ppdb" data-testid="hero-cta-register">
                <Button size="lg" variant="outline" className="bg-amber-500 hover:bg-amber-600 border-amber-500 text-[#0B1D3A] font-bold rounded-full px-7 h-12 shadow-xl shadow-amber-500/20">
                  Daftar Sekarang
                </Button>
              </Link>
            </div>

            <div className="mt-14 flex flex-wrap gap-x-10 gap-y-6 pt-8 border-t border-white/15">
              <div><div className="text-3xl font-serif font-bold text-amber-400"><Counter end={32} testId="stat-years" />+</div><div className="text-xs text-slate-300 uppercase tracking-wider mt-1">Tahun Dedikasi</div></div>
              <div><div className="text-3xl font-serif font-bold text-amber-400"><Counter end={stats.total_teachers || 32} testId="stat-teachers" /></div><div className="text-xs text-slate-300 uppercase tracking-wider mt-1">Guru & Staf</div></div>
              <div><div className="text-3xl font-serif font-bold text-amber-400">14</div><div className="text-xs text-slate-300 uppercase tracking-wider mt-1">Ruang Kelas</div></div>
              <div><div className="text-3xl font-serif font-bold text-amber-400"><Counter end={stats.total_achievements || 48} testId="stat-achievements" />+</div><div className="text-xs text-slate-300 uppercase tracking-wider mt-1">Prestasi</div></div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-widest uppercase animate-fade-in">
          Scroll <ChevronRight className="inline rotate-90 w-3 h-3" />
        </div>
      </section>

      {/* QUICK ACCESS */}
      <section data-testid="quick-info-section" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Akses Cepat</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">Jelajahi SMAS YPK 2 Biak</h2>
            </div>
            <p className="text-slate-600 max-w-md">Informasi penting bagi calon murid, orang tua, dan komunitas sekolah dalam satu klik.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {quickCards.map((c) => {
              const Icon = c.icon;
              return (
                <Link key={c.title} to={c.to} data-testid={`quick-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`} className="group card-elevated p-6 flex flex-col items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B1D3A] group-hover:text-amber-600 transition-colors">{c.title}</div>
                    <div className="mt-1 text-xs text-slate-500 flex items-center gap-1">Pelajari <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRINCIPAL MESSAGE */}
      <section data-testid="principal-section" className="py-16 lg:py-28 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 via-[#0B1D3A] to-amber-500" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={settings?.principal_photo_url || "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=600&q=85"}
                alt="Kepala Sekolah"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B1D3A] to-transparent p-6">
                <div className="text-amber-400 text-xs uppercase tracking-[0.2em] font-bold">Kepala Sekolah</div>
                <div className="text-white font-serif text-2xl font-bold mt-1">{settings?.principal_name || "Marice Sroyer, S.Pd"}</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Sambutan</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A] leading-tight">Pesan Kepala Sekolah</h2>
            <Quote className="w-10 h-10 text-amber-500/30 mt-6" />
            <p className="mt-3 text-lg text-slate-700 leading-relaxed font-light italic">
              {settings?.principal_message || "Selamat datang di SMAS YPK 2 Biak."}
            </p>
            <div className="mt-8 flex items-center gap-5">
              <div className="h-[1px] w-16 bg-amber-500" />
              <div>
                <div className="font-serif text-lg font-semibold text-[#0B1D3A]">{settings?.principal_name || "Marice Sroyer, S.Pd"}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Kepala SMAS YPK 2 Biak</div>
              </div>
            </div>
            <Link to="/tentang-kami" data-testid="principal-read-more" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0B1D3A] hover:text-amber-600 link-underline">
              Baca Sambutan Lengkap <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* GARAM VISION */}
      <section data-testid="garam-section" className="py-16 lg:py-28 bg-[#0B1D3A] relative overflow-hidden">
        <div className="absolute inset-0 papua-pattern-dark opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-400 mb-3">Visi Sekolah</div>
            <h2 className="font-serif text-3xl sm:text-5xl font-bold text-white leading-tight">
              Visi <span className="text-amber-400 italic">GARAM</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Terinspirasi dari metafora alkitabiah <em>"menjadi garam dunia"</em> — memberi rasa & mencegah kerusakan — kami membentuk generasi yang berdampak nyata di masyarakat.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {garam.map((g, idx) => (
              <div key={g.letter} data-testid={`garam-card-${g.letter}`} className="group relative bg-white/5 backdrop-blur-sm border border-white/10 hover:border-amber-500/40 rounded-2xl p-7 transition-all hover:bg-white/10">
                <div className="font-serif text-7xl font-black text-amber-400/90 leading-none">{g.letter}</div>
                <div className="mt-5">
                  <div className="font-serif font-bold text-xl text-white">{g.title}</div>
                  <div className="text-amber-300 text-sm font-semibold mt-1">{g.subtitle}</div>
                  <p className="mt-4 text-sm text-slate-300 leading-relaxed">{g.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 PILLARS */}
      <section data-testid="pillars-section" className="py-16 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Filosofi Pembelajaran</div>
              <h2 className="font-serif text-3xl sm:text-5xl font-bold text-[#0B1D3A] leading-tight">
                Learning at<br/>SMAS YPK 2 Biak
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Kami percaya pembelajaran bukan sekadar transfer ilmu, melainkan proses holistik yang membentuk 5 dimensi utama murid.
              </p>
              <Link to="/akademik" data-testid="pillars-cta">
                <Button className="mt-8 bg-[#0B1D3A] hover:bg-[#132D58] text-white rounded-full px-6 h-11">
                  Lihat Program Akademik <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {pillars.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={p.title} className={`card-elevated p-6 ${i === 1 ? "md:translate-y-6" : ""}`}>
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-serif text-xl font-bold text-[#0B1D3A]">{p.title}</div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section data-testid="programs-section" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Program Unggulan</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">Program Pembelajaran Berkualitas</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((p) => {
              const Icon = ICONS[p.icon] || Sparkles;
              return (
                <div key={p.id} data-testid={`program-${p.id}`} className="card-elevated p-7 group">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-lg shadow-amber-500/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="font-serif text-xl font-bold text-[#0B1D3A] mt-5">{p.name}</div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ACHIEVEMENTS */}
      <section data-testid="achievements-section" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Hall of Fame</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">Prestasi Terbaru</h2>
            </div>
            <Link to="/prestasi" data-testid="achievements-see-all" className="text-sm font-semibold text-[#0B1D3A] hover:text-amber-600 link-underline inline-flex items-center gap-2">
              Lihat Semua Prestasi <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {achievements.map((a) => (
              <div key={a.id} className="card-elevated p-6">
                <Trophy className="w-8 h-8 text-amber-500" />
                <div className="mt-4 flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">{a.level}</span>
                  <span className="text-slate-400">{a.year}</span>
                </div>
                <div className="font-serif text-lg font-bold text-[#0B1D3A] mt-3 leading-tight">{a.name}</div>
                <div className="text-xs text-slate-500 mt-2">{a.student_name} • {a.category}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section data-testid="news-section" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Terkini</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">Berita & Pengumuman</h2>
            </div>
            <Link to="/berita" data-testid="news-see-all" className="text-sm font-semibold text-[#0B1D3A] hover:text-amber-600 link-underline inline-flex items-center gap-2">
              Semua Berita <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {news.slice(0, 3).map((n, idx) => (
              <Link key={n.id} to={`/berita/${n.id}`} data-testid={`news-card-${n.id}`} className={`card-elevated overflow-hidden group flex flex-col ${idx === 0 ? "lg:row-span-2 lg:col-span-1" : ""}`}>
                <div className={`relative overflow-hidden ${idx === 0 ? "aspect-[4/5]" : "aspect-video"}`}>
                  <img src={n.thumbnail_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-4 left-4"><Badge className="bg-white/90 text-[#0B1D3A] font-semibold text-[11px] uppercase tracking-wider">{n.category}</Badge></div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-xs text-slate-500 flex items-center gap-2"><Calendar className="w-3 h-3" /> {new Date(n.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</div>
                  <h3 className="font-serif font-bold text-lg lg:text-xl text-[#0B1D3A] mt-2 leading-snug group-hover:text-amber-600 transition-colors">{n.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 line-clamp-2">{n.excerpt}</p>
                  <div className="mt-auto pt-4 text-xs font-semibold text-amber-600 inline-flex items-center gap-1">Baca Selengkapnya <ArrowRight className="w-3 h-3" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FOLLOW US */}
      <section data-testid="social-section" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-amber-600 mb-3">Ikuti Kami</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1D3A]">Tetap Terhubung dengan SMAYDA</h2>
            <p className="mt-4 text-slate-600">Ikuti kabar terbaru, momen kegiatan, prestasi, dan kehidupan sekolah lewat media sosial resmi kami.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <a href={settings?.instagram || "https://www.instagram.com/smayda_biak"} target="_blank" rel="noopener noreferrer"
               data-testid="social-instagram" className="group relative overflow-hidden rounded-2xl p-7 bg-gradient-to-br from-[#F77737] via-[#E1306C] to-[#833AB4] text-white transition-transform hover:-translate-y-1 shadow-lg">
              <Instagram className="w-9 h-9 mb-4" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-[0.22em] font-semibold opacity-80">Instagram</div>
              <div className="font-serif text-2xl font-bold mt-1">@smayda_biak</div>
              <div className="mt-6 text-sm font-semibold flex items-center gap-1 opacity-90">Ikuti sekarang <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full bg-white/10 pointer-events-none" />
            </a>
            <a href={settings?.facebook || "https://www.facebook.com/SmaydaBiak"} target="_blank" rel="noopener noreferrer"
               data-testid="social-facebook" className="group relative overflow-hidden rounded-2xl p-7 bg-gradient-to-br from-[#1877F2] to-[#0A4FB8] text-white transition-transform hover:-translate-y-1 shadow-lg">
              <Facebook className="w-9 h-9 mb-4" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-[0.22em] font-semibold opacity-80">Facebook</div>
              <div className="font-serif text-2xl font-bold mt-1">@SmaydaBiak</div>
              <div className="mt-6 text-sm font-semibold flex items-center gap-1 opacity-90">Like halaman kami <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full bg-white/10 pointer-events-none" />
            </a>
            <a href={settings?.youtube || "https://www.youtube.com/@smaypk2biak"} target="_blank" rel="noopener noreferrer"
               data-testid="social-youtube" className="group relative overflow-hidden rounded-2xl p-7 bg-gradient-to-br from-[#FF0000] to-[#B30000] text-white transition-transform hover:-translate-y-1 shadow-lg">
              <Youtube className="w-9 h-9 mb-4" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-[0.22em] font-semibold opacity-80">YouTube</div>
              <div className="font-serif text-2xl font-bold mt-1">@smaypk2biak</div>
              <div className="mt-6 text-sm font-semibold flex items-center gap-1 opacity-90">Subscribe channel <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full bg-white/10 pointer-events-none" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section data-testid="cta-ppdb" className="relative py-20 lg:py-28 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 papua-pattern-dark opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1D3A] via-[#132D58]/80 to-[#0B1D3A]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <Badge className="bg-amber-500 text-[#0B1D3A] font-bold rounded-full px-4 py-1.5 mb-6 uppercase tracking-wider text-xs">
            PPDB 2026/2027 — Dibuka
          </Badge>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05]">
            Jadilah Bagian dari<br/>
            <span className="text-amber-400 italic">Generasi GARAM</span>.
          </h2>
          <p className="mt-8 text-lg text-slate-300 max-w-2xl mx-auto">
            Daftarkan diri Anda sekarang dan raih masa depan yang berkarakter, berprestasi, dan berdampak di tanah Papua.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/ppdb" data-testid="cta-final-register">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1D3A] font-bold rounded-full px-8 h-12 shadow-xl shadow-amber-500/30">
                Daftar PPDB Online <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/kontak" data-testid="cta-final-contact">
              <Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0B1D3A] rounded-full px-8 h-12">
                Hubungi Kami
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
